import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useUsuario } from '../../context/usuarioContext';
import { useDocumentos } from '../../hooks/panel-vehiculo/useDocumentos';
import { useNavigate } from 'react-router-dom';
// Componentes UI
import { Toast } from 'primereact/toast';
import { TabMenu } from 'primereact/tabmenu';
import { IconField } from 'primereact/iconfield';
import { InputText } from 'primereact/inputtext';
import { InputIcon } from 'primereact/inputicon';
import { Tag } from 'primereact/tag';
// Componentes
import InformacionVeh from '../../components/panel-vehiculo/informacion-veh/informacion-veh';
import MantencionVeh from '../../components/panel-vehiculo/mantencion-veh/mantencion-veh';
import BitacoraVeh from '../../components/panel-vehiculo/bitacora-veh/bitacora-veh';
import DocumentacionVeh from '../../components/panel-vehiculo/documentacion-veh/documentacion-veh';
import VehiculoCard from '../../components/panel-vehiculo/vehiculo-card/vehiculo-card';
import TableLayout from "../../layouts/layout-tabla";
// Datos
import { obtenerBitacora, obtenerFlota } from '../../services/vehiculosService';
// Estilos y assets
import logo from "../../assets/logos/TRANSKYRIOS-_IconColor.avif";
import './panel-vehiculos.css';

const PanelVehiculos = () => {
  const toast = useRef(null);
  const navigate = useNavigate();
  const { tienePermiso } = useUsuario();
  const [tabActiva, setTabActiva] = useState(0);
  const [filtroPatente, setFiltroPatente] = useState("");
  const [vehiculosByPatente, setVehiculosByPatente] = useState({});
  const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState(null);
  const [regBitacora, setRegBitacora] = useState([]);
  const bitacoraCacheRef = useRef({});
  const TTL = 15000; // 15 segundos

  const handleShowToast = useCallback((msg) => {
    toast.current?.show(msg);
  }, []);

  const seleccionarVehiculo = useCallback((vehiculo) => { 
    setVehiculoSeleccionado(vehiculo);
  }, []);

  const { documentos, handleCargar, handleActualizar, handleDescargar, handleEliminar } = useDocumentos(vehiculoSeleccionado);

  const itemTabsVisibles = useMemo(() => {
    return itemsTab.filter(item => {
      if (!item.modulo) return true;
      return tienePermiso(item.modulo, item.permiso);
    });
  }, [tienePermiso]);

  const tabActual = itemTabsVisibles[tabActiva]?.key;

  const permisosInfo = useMemo(() => ({
    deshabilitar: tienePermiso('Panel Vehículos', 'VEH_DESHABILITAR_VEHICULO')
  }), [tienePermiso]);

  const permisosDoc = useMemo(() => ({
    cargar: tienePermiso('Panel Vehículos', 'VEH_DOC_CARGAR'),
    descargar: tienePermiso('Panel Vehículos', 'VEH_DOC_DESCARGAR'),
    actualizar: tienePermiso('Panel Vehículos', 'VEH_DOC_ACTUALIZAR'),
    eliminar: tienePermiso('Panel Vehículos', 'VEH_DOC_ELIMINAR'),
  }), [tienePermiso]);

  // Obtener vehículos registrados
  const getVehiculos = useCallback( async () => { 
    try {
      const res = await obtenerFlota();
      if (!Array.isArray(res)) throw new Error("Respuesta inesperada al obtener vehiculos");

      const map = {};
      res.forEach(v => {
        const patente = String(v.patente || "").toUpperCase().trim();
        if (!patente) return;
        map[patente] = { ...v, patente };
      });

      setVehiculosByPatente(map); // Información del vehiculo, accesible por patente
      setVehiculoSeleccionado(null);

    } catch (error) {
      toast.current?.show({
        severity: 'error', summary: 'Error', sticky: true,
        detail: 'No se pudieron cargar los vehículos'
      });
    }
  }, []);
  
  const vehiculos = useMemo(() => Object.values(vehiculosByPatente), [vehiculosByPatente]);
  
  const vehiculosFiltrados = useMemo(() => {
    const filtro = filtroPatente.toLowerCase();

    return vehiculos.filter(v =>
      v.patente.toLowerCase().includes(filtro) ||
      v.modelo?.toLowerCase().includes(filtro)
    );
  }, [vehiculos, filtroPatente]);

  useEffect(() => { getVehiculos(); }, [getVehiculos]);
  
  // Obtener bitacora del vehiculo
  const getBitacora = useCallback(async (patente) => {
    const cache = bitacoraCacheRef.current[patente];

    // si existe y no ha expirado
    if (cache && (Date.now() - cache.timestamp < TTL)) {
      setRegBitacora(cache.data);
      return;
    }

    try {
      const res = await obtenerBitacora(patente);

      bitacoraCacheRef.current[patente] = {
        data: res,
        timestamp: Date.now()
      };

      setRegBitacora(res);
    } catch (e) {
      console.error('Error al obtener el histórico', e);
    }
  }, []);

  useEffect(() => {
    if (vehiculoSeleccionado?.patente) {
      getBitacora(vehiculoSeleccionado.patente);
    }
  }, [vehiculoSeleccionado, getBitacora]);

  // Estado del vehículo
  const { estado_label, estado_severity, estado_icon } = getSeverity(vehiculoSeleccionado?.id_estado || 0);

  return (
    <TableLayout titulo="Flota Transkyrios" logo={logo} onVolver={() => navigate('/app')}>
      <Toast ref={toast} position='center'/>
      <section className='veh-container'>
        {/* SIDEBAR PATENTES */}
        <div className='veh-sidebar'>
          {/* Filtro patente  */}
          <div className="veh-sidebar-header">
            <div className="m-2">
              <IconField iconPosition="left">
                <InputIcon className="pi pi-search pr-2 pt-1" />
                <InputText 
                  value={filtroPatente} 
                  onChange={(e) => setFiltroPatente(e.target.value)}
                  placeholder='Buscar patente...'
                  className='pl-6 input-text-mm'
                />
              </IconField>

              {/* Texto de resultados */}
              <small className="text-500 justify-content-center d-flex mt-1">
                {filtroPatente
                  ? `${vehiculosFiltrados.length} resultado(s)`
                  : `Vehículos listados: ${vehiculos.length}`}
              </small>

              {filtroPatente && vehiculosFiltrados.length === 0 && (
                <small className= "justify-content-center d-flex required">
                  No se encontraron coincidencias
                </small>
              )}
            </div>
          </div>
          
          {/* Listado de patentes filtradas*/}
          <div className="veh-sidebar-body">
            {vehiculosFiltrados.map(v => (
              <VehiculoCard
                key={v.patente}
                v={v}
                seleccionado={vehiculoSeleccionado?.patente === v.patente}
                onClick={() => seleccionarVehiculo(v)}
              />
            ))}
          </div>
        </div>

        {/* COLUMNA DEL VEHICULO */}
        <div className='veh-content'>

          {/* HEADER */}
          <div className="veh-header">

            {vehiculoSeleccionado ? (
              <div className="veh-card-header">
                <div className="veh-info-status">
                  {/* Card Vehículo */}
                  <div className="d-flex align-items-center gap-3 ml-2">
                    {/* Icono */}
                    <div className="veh-icon-container">
                      <i className="pi pi-car veh-icon" />
                    </div>
                    {/* Info */}
                    <div>
                      <h5 className="mb-0 fw-semibold">
                        {vehiculoSeleccionado.patente}
                        <small className="badge veh-tag-tipovehiculo">
                          {vehiculoSeleccionado.tipo}
                        </small>
                      </h5>
                      <small className="text-muted">
                        {vehiculoSeleccionado.marca.toUpperCase()} {vehiculoSeleccionado.modelo.toUpperCase()} {vehiculoSeleccionado.anio}
                      </small>
                    </div>
                  </div>
                  {/* Estado */}
                  <Tag
                    className='veh-tag-estado'
                    value={estado_label}
                    severity={estado_severity}
                    icon={estado_icon}
                  />
                </div>
              </div>
            ) : (
              <div className="veh-card-header">
                <div className="veh-info-status">
                  <div className="d-flex align-items-center gap-3 ml-2">
                    <div className="avatar-no-seleccionado"></div>
                    <div>
                      <div className="barras-no-seleccionado mb-2"></div>
                      <div className="barras-no-seleccionado2 mb-2"></div>
                    </div>
                  </div>
                  <Tag className='veh-tag-estado' value= 'Seleccione un vehículo' severity= 'secondary' icon= 'pi pi-info-circle' />
                </div>
              </div>
            )}

            {/* Pestañas */}
            <TabMenu 
              model={itemTabsVisibles} 
              activeIndex={tabActiva} 
              onTabChange={(e) => setTabActiva(e.index)}
            />
          </div>

          {/* Contenido de la pestaña seleccionada*/}
          <div className="veh-body">
            { tabActual === 'informacion' && 
              <InformacionVeh 
                vehiculo={vehiculoSeleccionado}
                permisos={permisosInfo}
                onActualizar={getVehiculos}
                showToast={handleShowToast}
              />
            }
            
            { tabActual === 'mantencion' && 
              <MantencionVeh 
                vehiculo={vehiculoSeleccionado}
                mantencionesPrevias={mantencionesPrevias}
              />
            }

            { tabActual === 'historico' && 
              <BitacoraVeh 
                vehiculo={vehiculoSeleccionado} 
                registros={regBitacora}
                showToast={handleShowToast}
              />
            }

            { tabActual === 'documentacion' && 
              <DocumentacionVeh 
                vehiculo={vehiculoSeleccionado}
                documentos={documentos}
                permisos={permisosDoc}
                onDescargar={handleDescargar}
                onCargar={handleCargar}
                onActualizar={handleActualizar}
                onEliminar={handleEliminar}
                showToast={handleShowToast}
              />
            }
          </div>
        </div>
      </section>
    </TableLayout>
  );
}

export default PanelVehiculos;

const getSeverity = (estado) => {
  switch (estado) {
    case 0: return { estado_label: 'Sin información',               estado_severity: 'secondary', estado_icon: 'pi pi-question' };
    case 1: return { estado_label: 'Operativo',                     estado_severity: 'success',   estado_icon: 'pi pi-check' };
    case 2: return { estado_label: 'Mantenimiento',                 estado_severity: 'info',      estado_icon: 'pi pi-wrench' };
    case 3: return { estado_label: 'No operativo - Alerta Crítica', estado_severity: 'danger',    estado_icon: 'pi pi-exclamation-triangle' };
    case 4: return { estado_label: 'Alerta Media',                  estado_severity: 'warning',   estado_icon: 'pi pi-exclamation-circle' };
    default: return null;
  }
};

const itemsTab = [
  { label: 'Información', icon: 'pi pi-home', key: 'informacion', modulo: 'Panel Vehículos', permiso: 'VER_INFO_VEH'}, 
  { label: 'Mantención', icon: 'pi pi-wrench', key: 'mantencion', modulo: 'Panel Vehículos', permiso: 'VER_MANTENCION_VEH'  }, 
  { label: 'Histórico', icon: 'pi pi-book', key: 'historico', modulo: 'Panel Vehículos', permiso: 'VER_BITACORA_VEH'  }, 
  { label: 'Documentación', icon: 'pi pi-inbox', key: 'documentacion', modulo: 'Panel Vehículos', permiso: 'VER_DOCUMENTACION_VEH' }, 
];

// Dumies Mantenciones Previas
const mantencionesPrevias = { aceite:220000, neumaticos:190000, refrigerante:228000, frenos:205000 };